<script setup lang="ts">
import type { StoryblokRichTextNode } from '@storyblok/vue'
import type { StoryblokAsset } from '../../types'

interface Props {
  blok: {
    badge?: any[]
    title: StoryblokRichTextNode
    subtitle: StoryblokRichTextNode
    actions: any[]
    features: any[]
    youtube_link: string
    video_cover: StoryblokAsset
  }
}

const { blok } = defineProps<Props>()

// Estado para ativação do vídeo
const isVideoActive = ref(false)

// Ativa o vídeo ao clicar no botão Play
const activateVideo = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  isVideoActive.value = true
}

const videoId = computed(() => extractYouTubeVideoId(blok.youtube_link))

const embedUrl = computed(() =>
  videoId.value
    ? `https://www.youtube.com/embed/${videoId.value}?autoplay=1&mute=0&controls=0&loop=0&playlist=${videoId.value}&modestbranding=0&showinfo=0&rel=0&iv_load_policy=3`
    : '',
)
</script>

<template>
  <section
    id="hero-6"
    v-editable="blok"
    :class="$style.container"
  >
    <div class="flex flex-col gap-5 md:gap-10 py-10 md:py-15">
      <div v-if="blok.badge && blok.badge.length" class="flex flex-col md:flex-row items-center justify-center gap-3">
        <StoryblokComponent v-for="badge in blok.badge" :key="badge._uid" :blok="badge" />
      </div>

      <div text-center class="font-circular-bold">
        <StoryblokRichText v-if="blok.title" :doc="blok.title" />
      </div>

      <div v-if="blok.subtitle" class="text-center text-[16px] font-medium text-[#21242C] leading-[28px] w-full max-w-[830px] mx-auto">
        <StoryblokRichText :doc="blok.subtitle" />
      </div>
      <div class="flex flex-col gap-5">
        <div v-if="blok.actions.length" center gap-3 mx-auto>
          <StoryblokComponent v-for="action in blok.actions" :key="action._uid" :blok="action" />
        </div>
        <div v-if="blok.features.length" center flex-wrap gap-3 mx-auto>
          <StoryblokComponent v-for="feature in blok.features" :key="feature._uid" :blok="feature" />
        </div>
      </div>
    </div>

    <div
      class="w-full max-w-600px xl:max-w-1280px aspect-video rounded-5 overflow-hidden mx-auto shadow-lg relative pb-[25%] videoContainer"
    >
      <button
        v-if="!isVideoActive"
        class="absolute inset-0 flex items-center justify-center z-20 bg-black/0 hover:bg-black/20 group transition-all"
        aria-label="Play Video"
        type="button"
        @click="activateVideo"
      >
        <IconPlay class="w-16 h-16 text-white opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-100 transition-all" />
      </button>

      <iframe
        v-if="isVideoActive"
        class="absolute inset-0 z-10 w-full opacity-1 h-full animate-fade-in animate-forwards animate-delay-400 animate-duration-400"
        :src="embedUrl"
        frameborder="0" allow="autoplay; encrypted-media" allowfullscreen
      />

      <img
        class="object-cover absolute inset-0 w-full h-full opacity-0 transition-delay-300 duration-100"
        :class="{ '!opacity-100 !transition-delay-0 !duration-0': !isVideoActive }"
        :src="blok.video_cover.filename"
      >
    </div>
  </section>
</template>

<style  module lang="scss">
  .videoContainer {
    cursor: pointer;
  }
  .container {
    background: #FFF;
    padding: 0 10px 30px;

    @screen md {
      padding: 0 60px 60px;
    }

    @screen lg {
      padding: 0 60px 60px;
    }

    @screen 2xl {
      padding: 0 128px 60px;
    }
  }

  .heroImage {
    border-radius: 5.358px 5.358px 0px 0px;
    box-shadow: 0px 0px 16.073px 0px rgba(0, 0, 0, 0.05);

    @screen lg {
      border-radius: 20px 20px 0px 0px;
      box-shadow: 0px 0px 60px 0px rgba(0, 0, 0, 0.05);
    }
  }
  </style>
